import { useEffect, useState, useCallback } from "react";
import { CalendarOff, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = { NASIONAL: "Libur Nasional", PERUSAHAAN: "Libur Perusahaan", CUTI_BERSAMA: "Cuti Bersama" };

export function HolidaysCard() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", date: "", type: "NASIONAL", description: "" });

  const load = useCallback(() => { api.get("/holidays").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.name || !f.date) return toast.error("Nama dan tanggal wajib diisi");
    try { await api.post("/holidays", f); toast.success("Hari libur ditambahkan"); setF({ name: "", date: "", type: "NASIONAL", description: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/holidays/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <Card className="rounded-2xl border-slate-200 p-6 dark:border-slate-800">
      <div className="mb-4 flex items-center gap-2">
        <CalendarOff className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-lg font-semibold">Kalender Hari Libur</h3>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
        <Input data-testid="holiday-name-input" placeholder="Nama hari libur" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid="holiday-date-input" type="date" className="w-40" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
          <SelectTrigger data-testid="holiday-type-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="add-holiday-button" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="mt-4 space-y-2">
        {list.length === 0 && <p className="text-sm text-slate-400">Belum ada hari libur terdaftar.</p>}
        {list.map((h) => (
          <div key={h.id} data-testid="holiday-item" className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-800/40">
            <div>
              <span className="text-sm font-medium">{h.name}</span>
              <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300">{TYPES[h.type] || h.type}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-slate-500">{fmtDate(h.date)}</span>
              <Button size="icon" variant="ghost" onClick={() => del(h.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
